// src/components/forms/CreateFormFromReportDialog.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Loader2, FileUp, Sparkles } from "lucide-react";
import mammoth from "mammoth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import { useFormsStore } from "@/store/formsStore";
import type { FormField, FieldType } from "@/types/form";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GeneratedField {
  type: FieldType;
  label: string;
  required?: boolean;
  options?: string[];
}

interface GeneratedForm {
  title: string;
  description?: string;
  fields: GeneratedField[];
}

export default function CreateFormFromReportDialog({ open, onOpenChange }: Props) {
  const { createForm, updateForm } = useFormsStore();

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState<GeneratedForm | null>(null);
  const [creating, setCreating] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setGenerated(null);
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast({
        title: "Ошибка",
        description: "Выберите файл .docx",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setGenerated(null);

    try {
      // 1. Читаем текст из docx
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const reportText = result.value;

      if (!reportText || reportText.trim().length < 50) {
        toast({
          title: "Ошибка",
          description: "Не удалось извлечь текст из файла",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // 2. Вызываем Cloud Function
      const functions = getFunctions(undefined, "us-central1");
      const generateForm = httpsCallable(functions, "generateFormFromReport");

      const response = await generateForm({ reportText });
      const data = (response.data as any)?.data as GeneratedForm;

      if (!data || !data.fields) {
        throw new Error("Нейросеть вернула пустой результат");
      }

      setGenerated(data);
      toast({ title: "Готово!", description: "Поля успешно сгенерированы" });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Ошибка",
        description: err.message || "Не удалось проанализировать отчёт",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    if (!generated || !file) return;

    setCreating(true);

    try {
      // 1. Превращаем поля в нужный формат
      const fields: FormField[] = generated.fields.map((f) => ({
        id: Math.random().toString(36).substring(2, 10),
        type: f.type || "text",
        label: f.label,
        required: f.required ?? false,
        options: f.options,
      }));

      // 2. Создаём форму
      const formId = await createForm({
        title: generated.title || "Новая акция",
        description: generated.description || "",
        fields,
      });

      if (!formId) {
        toast({
          title: "Ошибка",
          description: "Не удалось создать форму",
          variant: "destructive",
        });
        return;
      }

      // 3. Загружаем оригинальный шаблон в Storage
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `forms/${formId}/template/${timestamp}_${safeName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const templateUrl = await getDownloadURL(storageRef);

      // 4. Сохраняем ссылку на шаблон в форме
      await updateForm(formId, {
        templateUrl,
        templatePath: storagePath,
      });

      toast({ title: "Форма создана!", description: "Шаблон отчёта сохранён" });
      onOpenChange(false);
      setFile(null);
      setGenerated(null);
    } catch (err) {
      console.error(err);
      toast({
        title: "Ошибка",
        description: "Не удалось создать форму",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-950 border-purple-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-400" />
            Создать форму из отчёта
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Загрузка файла */}
          <div className="space-y-2">
            <Label>Загрузите готовый отчёт (.docx)</Label>
            <Input
              type="file"
              accept=".docx"
              onChange={handleFileChange}
              className="bg-purple-950/50 border-purple-600 text-white"
            />
            {file && (
              <p className="text-sm text-purple-300">
                Выбран файл: {file.name}
              </p>
            )}
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={!file || loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Анализируем отчёт...
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4 mr-2" />
                Проанализировать и предложить поля
              </>
            )}
          </Button>

          {/* Результат генерации */}
          {generated && (
            <div className="space-y-4 border-t border-purple-800 pt-6">
              <div>
                <h3 className="text-lg font-semibold text-purple-200 mb-1">
                  {generated.title}
                </h3>
                {generated.description && (
                  <p className="text-sm text-purple-400">{generated.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Предложенные поля ({generated.fields.length}):</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {generated.fields.map((field, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-purple-950/40 border border-purple-700/50 rounded-xl px-4 py-3"
                    >
                      <div>
                        <span className="text-white font-medium">{field.label}</span>
                        {field.required && (
                          <span className="text-red-400 ml-1">*</span>
                        )}
                      </div>
                      <Badge variant="secondary" className="capitalize">
                        {field.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-purple-600 text-purple-200"
          >
            Отмена
          </Button>

          {generated && (
            <Button
              onClick={handleCreateForm}
              disabled={creating}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
            >
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Создать форму
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}